import { useContext } from 'react';
import { MeContext } from '../context/MeContext';
import Prompt from '../common/Prompt';
import EmptyState from '../common/EmptyState';
import { Text, Link } from '@chakra-ui/react';
import { useIsReadOnly } from './useIsReadOnly';

const useOrganizationStatus = (iconSvgAnimated = null, onGoLive = null) => {
  const meCtx = useContext(MeContext);
  const {
    organisationStatus,
    subscribed,
    billingEnabled,
    cardlessTrialStartedAt,
  } = meCtx.state;
  const isSetUpConfirmed = meCtx?.isSetUpConfirmed() ?? true;
  const isOrganisationActive = meCtx?.isOrganisationActive() ?? true;
  const isReadOnly = useIsReadOnly();
  let components = [];
  // Handling specific scenarios
  if (!isSetUpConfirmed) {
    components.push(
      <Prompt
        key="setup-progress"
        title="Your account setup is in progress"
        description={
          <Text fontSize="14px">
            If you would like an update please reach out to{' '}
            <Link href="mailto:sales@passentry.com" color="">
              sales@passentry.com
            </Link>
            .
          </Text>
        }
      />
    );
  }

  if (
    !isReadOnly &&
    isSetUpConfirmed &&
    organisationStatus === 'trial_expired' &&
    !cardlessTrialStartedAt
  ) {
    components.push(
      <Prompt
        key="trial-expired"
        title="Your trial period has expired"
        description={
          <Text fontSize="14px">
            Please subscribe to a plan or reach out to{' '}
            <Link href="mailto:sales@passentry.com" color="">
              sales@passentry.com
            </Link>{' '}
            to continue using our services.
          </Text>
        }
      />
    );
  }

  if (
    !isReadOnly &&
    ((isSetUpConfirmed && organisationStatus === 'locked') ||
      (organisationStatus === 'trial_expired' && cardlessTrialStartedAt))
  ) {
    components.push(
      <Prompt
        key="account-frozen"
        title="Your account is currently frozen."
        description={
          <Text fontSize="14px">
            Please subscribe to a plan or reach out to{' '}
            <Link href="mailto:sales@passentry.com" color="">
              sales@passentry.com
            </Link>{' '}
            to continue using our services.
          </Text>
        }
      />
    );
  }

  // Only show subscription required prompt if billing is enabled
  if (
    !isReadOnly &&
    billingEnabled === true &&
    !subscribed &&
    !cardlessTrialStartedAt
  ) {
    components.push(
      <Prompt
        key="subscription-required"
        title="Subscription Required"
        description={
          <Text fontSize="14px">
            Please{' '}
            <Link
              href="/subscription"
              color="blue.500"
              textDecoration="underline"
            >
              subscribe
            </Link>{' '}
            to use our services.
          </Text>
        }
      />
    );
  }

  if (
    !isSetUpConfirmed ||
    (!isReadOnly && !isOrganisationActive && !cardlessTrialStartedAt)
  ) {
    components.push(
      <EmptyState
        key="empty-state"
        iconSvgAnimated={iconSvgAnimated ? iconSvgAnimated : null}
        heading="No data available."
        text="You will be able to see it here once your account is fully set up."
      />
    );
  }

  return {
    shouldRender:
      components.length === 0 ||
      (components.length === 1 && organisationStatus === 'trial' && subscribed),
    components,
  };
};

export default useOrganizationStatus;
